import { useEffect, useRef } from "react";

/**
 * Six unique solution motifs: scan, structure, cluster, grid, selected, wave.
 * Used on Solutions page cards.
 */
export default function Motif({ kind, height = 160 }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      c.width = c.clientWidth * dpr; c.height = c.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize(); window.addEventListener("resize", resize);
    const lerp = (a, b, t) => a + (b - a) * t;
    let raf, t = 0;

    const draw = () => {
      const w = c.clientWidth, h = c.clientHeight;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2, cy = h / 2;

      if (kind === "scan") {
        const ang = (t * 0.015) % (Math.PI * 2);
        ctx.strokeStyle = "rgba(157,180,255,0.25)";
        for (let r = 30; r < 90; r += 20) {
          ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
        }
        ctx.strokeStyle = "rgba(157,180,255,0.85)";
        ctx.beginPath(); ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(ang) * 90, cy + Math.sin(ang) * 90); ctx.stroke();
        for (let i = 0; i < 7; i++) {
          const a = i * 1.1, r = 30 + i * 8;
          ctx.fillStyle = Math.abs((ang - a + Math.PI * 2) % (Math.PI * 2)) < 0.4 ? "#E8ECF1" : "#9DB4FF";
          ctx.beginPath(); ctx.arc(cx + Math.cos(a) * r, cy + Math.sin(a) * r, 2.2, 0, Math.PI * 2); ctx.fill();
        }
      } else if (kind === "structure") {
        for (let i = 0; i < 60; i++) {
          const phase = (t * 0.01 + i * 0.06) % 1;
          const targetRow = i % 5;
          const x = phase * (w - 30) + 15;
          const yChaos = cy + Math.sin(i + t * 0.02) * 30;
          const yOrder = 30 + targetRow * ((h - 60) / 4);
          const y = lerp(yChaos, yOrder, phase);
          ctx.fillStyle = `rgba(157,180,255,${0.4 + phase * 0.5})`;
          ctx.beginPath(); ctx.arc(x, y, 1.3, 0, Math.PI * 2); ctx.fill();
        }
      } else if (kind === "cluster") {
        const sub = 6;
        for (let i = 0; i < sub; i++) {
          const a = (i / sub) * Math.PI * 2;
          const x = cx + Math.cos(a) * 50, y = cy + Math.sin(a) * 50;
          ctx.strokeStyle = "rgba(157,180,255,0.4)"; ctx.lineWidth = 0.6;
          ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(x, y); ctx.stroke();
          const tt = ((t / 60 + i * 0.18) % 1);
          const px = cx + (x - cx) * tt, py = cy + (y - cy) * tt;
          ctx.fillStyle = "#E8ECF1";
          ctx.beginPath(); ctx.arc(px, py, 1.6, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = "#9DB4FF";
          ctx.beginPath(); ctx.arc(x, y, 2.5, 0, Math.PI * 2); ctx.fill();
        }
        ctx.fillStyle = "#E8ECF1";
        ctx.beginPath(); ctx.arc(cx, cy, 4, 0, Math.PI * 2); ctx.fill();
      } else if (kind === "grid") {
        const cols = 9, rows = 6, gx = (w - 40) / (cols - 1), gy = (h - 40) / (rows - 1);
        for (let i = 0; i < cols; i++) {
          for (let j = 0; j < rows; j++) {
            const phase = (t * 0.02 + i * 0.2 + j * 0.15) % 4;
            const active = phase < 1;
            ctx.fillStyle = active
              ? `rgba(157,180,255,${0.9 - phase * 0.7})`
              : "rgba(232,236,241,0.12)";
            ctx.fillRect(20 + i * gx - 1.5, 20 + j * gy - 1.5, 3, 3);
          }
        }
      } else if (kind === "selected") {
        for (let i = 0; i < 16; i++) {
          const x = 20 + (i % 8) * ((w - 40) / 7);
          const y = 30 + Math.floor(i / 8) * 60;
          const sel = [1, 4, 7, 9, 13].includes(i);
          ctx.fillStyle = sel ? "#9DB4FF" : "rgba(232,236,241,0.18)";
          ctx.beginPath(); ctx.arc(x, y, sel ? 3 : 2, 0, Math.PI * 2); ctx.fill();
          if (sel) {
            ctx.strokeStyle = "rgba(157,180,255,0.4)";
            ctx.beginPath(); ctx.arc(x, y, 8 + Math.sin(t / 20 + i) * 2, 0, Math.PI * 2); ctx.stroke();
          }
        }
      } else if (kind === "wave") {
        ctx.strokeStyle = "#9DB4FF"; ctx.lineWidth = 1;
        ctx.beginPath();
        for (let x = 0; x < w; x += 3) {
          const y = cy + Math.sin((x + t * 1.4) * 0.04) * 18 + Math.sin((x + t) * 0.012) * 10;
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
        for (let i = 0; i < 6; i++) {
          const x = 15 + i * ((w - 30) / 5);
          ctx.fillStyle = "rgba(232,236,241,0.4)";
          ctx.fillRect(x, h - 18, 1, 6);
        }
      }
      t += 1;
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [kind]);
  return <canvas ref={ref} className="w-full block" style={{ width: "100%", height }} />;
}
